import { useState, useRef, useEffect } from 'react';
import {
  MessageSquare,
  Send,
  X,
  Loader2,
  Bot,
  User,
  Sparkles,
  BarChart3,
  Zap,
  AlertTriangle,
  CheckCircle2,
  Plus,
  ArrowRight,
  Trash2,
} from 'lucide-react';
import { Button, useToast } from '../ui';
import { useConfigurator } from '../../hooks/useConfigurator';
import { useChatHistory, type ChatMessage } from '../../hooks/useChatHistory';
import { generateRecommendation, parseUserIntent } from '../../services/aiRecommendations';
import {
  streamChat,
  analyzeConfig,
  checkChatHealth,
  type ConfigContext,
  type ConfigAnalysisResponse,
} from '../../services/api';
import { DatacenterConfig, NetworkConfig, SecurityConfig } from '../../types/configurator';
import { clsx } from 'clsx';

type WizardStepName = 'scenario' | 'network' | 'nodes' | 'services' | 'security' | 'summary';

// Quick suggestions for each wizard step
const STEP_SUGGESTIONS: Record<WizardStepName, string[]> = {
  scenario: [
    'Which scenario fits my use case?',
    'What\'s the difference between minimal and research?',
    'I need a cluster for machine learning',
  ],
  network: [
    'Help me plan IP addressing',
    'What subnet should I use?',
    'Explain the DNS setup',
  ],
  nodes: [
    'Add more compute nodes',
    'What hardware do I need for ML?',
    'Recommend a storage configuration',
  ],
  services: [
    'Which services are essential?',
    'Where should MinIO run?',
    'Explain the container registry',
  ],
  security: [
    'Set up VPN access',
    'Should I enable storage encryption?',
    'Best practices for airgapped security',
  ],
  summary: [
    'Review my configuration',
    'Any recommendations before export?',
    'Explain the power budget',
  ],
};

interface AIAction {
  label: string;
  step?: WizardStepName;
  action?: 'analyze' | 'export' | 'addNode';
  nodeType?: 'gateway' | 'compute' | 'storage' | 'daq';
}

// Re-export ChatMessage as Message for compatibility
type Message = ChatMessage & {
  analysis?: ConfigAnalysisResponse;
};

// Parse AI response for action hints (e.g., "[Go to Security →]" or "[Step 4: Services]")
function parseActionsFromContent(content: string): { cleanContent: string; actions: AIAction[] } {
  const actions: AIAction[] = [];

  // Map keywords to wizard step names
  const stepMap: Record<string, WizardStepName> = {
    'scenario': 'scenario', 'scenarios': 'scenario',
    'network': 'network', 'networking': 'network',
    'nodes': 'nodes', 'node': 'nodes', 'hardware': 'nodes',
    'services': 'services', 'service': 'services',
    'security': 'security',
    'summary': 'summary', 'review': 'summary', 'export': 'summary',
  };

  // Map keywords to node types
  const nodeTypeMap: Record<string, 'gateway' | 'compute' | 'storage' | 'daq'> = {
    'gateway': 'gateway', 'router': 'gateway',
    'compute': 'compute', 'worker': 'compute', 'server': 'compute',
    'storage': 'storage', 'minio': 'storage', 'nas': 'storage',
    'daq': 'daq', 'acquisition': 'daq', 'sensor': 'daq',
  };

  let cleanContent = content;

  // Detect node addition suggestions
  const nodePatterns = [
    /add (?:a |another )?(?:second |additional )?(\w+)(?: node)?/gi,
    /recommend(?:ing)? (?:a |adding )?(?:second |another )?(\w+)(?: node)?/gi,
    /consider (?:adding )?(?:a )?(?:second )?(\w+)(?: node)?/gi,
  ];

  for (const pattern of nodePatterns) {
    let match;
    while ((match = pattern.exec(content)) !== null) {
      const word = match[1].toLowerCase();
      const nodeType = nodeTypeMap[word];
      if (nodeType && !actions.some(a => a.nodeType === nodeType)) {
        const labels: Record<string, string> = {
          gateway: 'Add Gateway',
          compute: 'Add Compute Node',
          storage: 'Add Storage Node',
          daq: 'Add DAQ Node',
        };
        actions.push({
          label: labels[nodeType],
          action: 'addNode',
          nodeType,
        });
      }
    }
  }

  // Extract bracketed actions and clean them from content
  const bracketPattern = /\[([^\]]+)\]/g;
  let match;
  while ((match = bracketPattern.exec(content)) !== null) {
    const inner = match[1].toLowerCase();
    for (const [key, step] of Object.entries(stepMap)) {
      if (inner.includes(key)) {
        actions.push({
          label: `Go to ${key.charAt(0).toUpperCase() + key.slice(1)}`,
          step,
        });
        cleanContent = cleanContent.replace(match[0], '').trim();
        break;
      }
    }
  }

  // Match patterns like "→ Security step" or "go to the Network section"
  const patterns = [
    /(?:navigate|go) to (?:the )?(\w+)(?: step| section| wizard step)?/gi,
    /(?:let's |I recommend |you should )(?:configure|set up|check) (?:the )?(\w+)/gi,
  ];

  // Look for navigation suggestions in prose
  for (const pattern of patterns) {
    while ((match = pattern.exec(content)) !== null) {
      const word = match[1].toLowerCase();
      if (stepMap[word] !== undefined && !actions.some(a => a.step === stepMap[word])) {
        actions.push({
          label: `Go to ${word.charAt(0).toUpperCase() + word.slice(1)}`,
          step: stepMap[word],
        });
      }
    }
  }

  // Deduplicate by step (keep node actions separate)
  const stepActions = actions.filter(a => a.step);
  const nodeActions = actions.filter(a => a.nodeType);
  const uniqueSteps = stepActions.filter((a, i) => stepActions.findIndex(b => b.step === a.step) === i);
  const uniqueNodes = nodeActions.filter((a, i) => nodeActions.findIndex(b => b.nodeType === a.nodeType) === i);

  return { cleanContent, actions: [...uniqueNodes, ...uniqueSteps].slice(0, 4) }; // Max 4 actions
}

type TabType = 'chat' | 'analyze';

export function AIAssistant() {
  const [isOpen, setIsOpen] = useState(false);
  const [activeTab, setActiveTab] = useState<TabType>('chat');
  const [isDemo, setIsDemo] = useState(false);
  const [aiAvailable, setAiAvailable] = useState<boolean | null>(null);
  const { messages, setMessages, clearHistory } = useChatHistory();
  const [input, setInput] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [isStreaming, setIsStreaming] = useState(false);
  const [analysis, setAnalysis] = useState<ConfigAnalysisResponse | null>(null);
  const [isAnalyzing, setIsAnalyzing] = useState(false);
  const messagesEndRef = useRef<HTMLDivElement>(null);
  const streamControllerRef = useRef<AbortController | null>(null);
  const { config, currentStep, setScenario, addNode, updateNetwork, updateSecurity, goToStep } = useConfigurator();
  const { success } = useToast();

  // Check AI availability on mount with quick timeout
  useEffect(() => {
    let isMounted = true;

    // Use a shorter initial check to avoid blocking UI
    const checkHealth = async () => {
      try {
        const health = await checkChatHealth();
        if (isMounted) {
          setAiAvailable(health.ollama);
        }
      } catch {
        if (isMounted) {
          setAiAvailable(false);
        }
      }
    };

    // Delay health check slightly to avoid blocking initial render
    const timeoutId = setTimeout(checkHealth, 100);

    return () => {
      isMounted = false;
      clearTimeout(timeoutId);
    };
  }, []);

  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages]);

  const buildContext = (cfg: DatacenterConfig): ConfigContext => ({
    scenario: cfg.scenario,
    nodeCount: cfg.nodes.length,
    nodes: cfg.nodes.map((n) => ({ type: n.type, hostname: n.hostname, ip: n.ip })),
    estimatedPower: cfg.estimatedPower,
    estimatedCost: cfg.estimatedCost,
    services: cfg.serviceAllocations.map((a) => a.serviceId),
    vpnEnabled: cfg.security.vpnEnabled,
    currentStep,
  });

  // Build message history for the API (excluding the welcome message)
  const buildMessageHistory = (): { role: 'user' | 'assistant'; content: string }[] => {
    return messages
      .filter((m) => m.id !== 'welcome')
      .map((m) => ({ role: m.role, content: m.content }));
  };

  const handleSend = async () => {
    if (!input.trim() || isLoading) return;

    const userInput = input.trim();
    const userMessage: Message = {
      id: `user-${Date.now()}`,
      role: 'user',
      content: userInput,
      timestamp: Date.now(),
    };

    setMessages((prev) => [...prev, userMessage]);
    setInput('');
    setIsLoading(true);

    // If AI not available, use rule-based immediately
    if (aiAvailable === false) {
      if (!isDemo) setIsDemo(true);
      const response = applyRuleBasedResponse(userInput, config);
      const assistantMessage: Message = {
        id: `assistant-${Date.now()}`,
        role: 'assistant',
        content: response,
        timestamp: Date.now(),
      };
      setMessages((prev) => [...prev, assistantMessage]);
      setIsLoading(false);
      return;
    }

    // Create placeholder message for streaming
    const assistantId = `assistant-${Date.now()}`;
    const assistantMessage: Message = {
      id: assistantId,
      role: 'assistant',
      content: '',
      timestamp: Date.now(),
    };
    setMessages((prev) => [...prev, assistantMessage]);

    let streamFailed = false;

    // Build message history including the new user message
    const history = [...buildMessageHistory(), { role: 'user' as const, content: userInput }];

    // Start streaming with conversation history
    const controller = streamChat(
      userInput,
      buildContext(config),
      history,
      {
        onChunk: (content) => {
          setIsStreaming(true);
          // Update the assistant message with new content
          setMessages((prev) =>
            prev.map((m) =>
              m.id === assistantId
                ? { ...m, content: m.content + content }
                : m
            )
          );
        },
        onError: (error) => {
          console.error('Stream error:', error);
          streamFailed = true;
          // Fall back to rule-based on error
          if (!isDemo) setIsDemo(true);
          const fallbackResponse = applyRuleBasedResponse(userInput, config);
          setMessages((prev) =>
            prev.map((m) =>
              m.id === assistantId
                ? { ...m, content: fallbackResponse }
                : m
            )
          );
          setIsLoading(false);
          setIsStreaming(false);
          streamControllerRef.current = null;
        },
        onDone: () => {
          if (!streamFailed) {
            if (isDemo) setIsDemo(false);
            // Parse actions from the completed response
            setMessages((prev) =>
              prev.map((m) => {
                if (m.id === assistantId && m.content) {
                  const { cleanContent, actions } = parseActionsFromContent(m.content);
                  return { ...m, content: cleanContent, actions: actions.length > 0 ? actions : undefined };
                }
                return m;
              })
            );
          }
          setIsLoading(false);
          setIsStreaming(false);
          streamControllerRef.current = null;
        },
      }
    );

    // Store controller for cancellation
    streamControllerRef.current = controller;
  };

  const handleCancelStream = () => {
    if (streamControllerRef.current) {
      streamControllerRef.current.abort();
      streamControllerRef.current = null;
      setIsLoading(false);
      setIsStreaming(false);
    }
  };

  const applyRuleBasedResponse = (input: string, config: DatacenterConfig): string => {
    const intent = parseUserIntent(input);
    const recommendation = generateRecommendation(intent, config);

    // Apply actions
    if (recommendation.actions) {
      for (const action of recommendation.actions) {
        switch (action.type) {
          case 'setScenario':
            if (action.value) setScenario(action.value);
            break;
          case 'addNode':
            if (action.nodeType) addNode(action.nodeType, action.specs, action.role);
            break;
          case 'goToStep':
            if (action.step) goToStep(action.step);
            break;
          case 'updateNetwork':
            if (action.updates) updateNetwork(action.updates as Partial<NetworkConfig>);
            break;
          case 'updateSecurity':
            if (action.updates) updateSecurity(action.updates as Partial<SecurityConfig>);
            break;
        }
      }
    }

    return recommendation.message;
  };

  const handleAnalyze = async () => {
    if (isAnalyzing) return;
    setIsAnalyzing(true);

    try {
      const result = await analyzeConfig(buildContext(config));
      setAnalysis(result);
    } catch (error) {
      console.error('Analysis error:', error);
      setAnalysis({
        success: false,
        score: 0,
        categories: [],
        recommendations: [],
        warnings: [{ severity: 'critical', message: 'Analysis service unavailable' }],
        summary: 'Could not analyze configuration. The AI service may be offline.',
      });
    } finally {
      setIsAnalyzing(false);
    }
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSend();
    }
  };

  const getScoreColor = (score: number) => {
    if (score >= 70) return 'text-green-600';
    if (score >= 40) return 'text-yellow-600';
    return 'text-red-600';
  };

  const getStatusIcon = (status: string) => {
    switch (status) {
      case 'good':
        return <CheckCircle2 className="h-4 w-4 text-green-500" />;
      case 'warning':
        return <AlertTriangle className="h-4 w-4 text-yellow-500" />;
      case 'critical':
        return <X className="h-4 w-4 text-red-500" />;
      default:
        return null;
    }
  };

  return (
    <>
      {/* Toggle Button */}
      <button
        onClick={() => setIsOpen(!isOpen)}
        className={clsx(
          'fixed bottom-6 right-6 z-50 flex h-14 w-14 items-center justify-center rounded-full shadow-lg transition-all',
          isOpen
            ? 'bg-gray-600 text-white hover:bg-gray-700'
            : 'bg-hermit-600 text-white hover:bg-hermit-700'
        )}
      >
        {isOpen ? <X className="h-6 w-6" /> : <MessageSquare className="h-6 w-6" />}
      </button>

      {/* Panel */}
      {isOpen && (
        <div className="fixed bottom-24 right-6 z-50 flex h-[520px] w-[400px] flex-col overflow-hidden rounded-xl border border-gray-200 bg-white shadow-2xl">
          {/* Header */}
          <div className="flex items-center gap-3 border-b border-gray-200 bg-hermit-600 px-4 py-3 text-white">
            <div className="flex h-8 w-8 items-center justify-center rounded-full bg-white/20">
              <Sparkles className="h-5 w-5" />
            </div>
            <div className="flex-1">
              <h3 className="font-semibold">AI Assistant</h3>
              <p className="text-xs text-hermit-200">
                {aiAvailable === null ? 'Checking...' : aiAvailable ? 'Connected to Ollama' : 'Demo Mode'}
              </p>
            </div>
            {isDemo && (
              <span className="rounded-full bg-yellow-500 px-2 py-0.5 text-xs font-medium text-yellow-900">
                Demo
              </span>
            )}
            {messages.length > 1 && (
              <button
                onClick={() => {
                  clearHistory();
                  success('Chat history cleared');
                }}
                className="rounded-full p-1.5 hover:bg-white/20 transition-colors"
                title="Clear chat history"
              >
                <Trash2 className="h-4 w-4" />
              </button>
            )}
          </div>

          {/* Tabs */}
          <div className="flex border-b border-gray-200">
            <button
              onClick={() => setActiveTab('chat')}
              className={clsx(
                'flex-1 px-4 py-2 text-sm font-medium transition-colors',
                activeTab === 'chat'
                  ? 'border-b-2 border-hermit-600 text-hermit-600'
                  : 'text-gray-500 hover:text-gray-700'
              )}
            >
              <MessageSquare className="mr-1.5 inline h-4 w-4" />
              Chat
            </button>
            <button
              onClick={() => {
                setActiveTab('analyze');
                if (!analysis && !isAnalyzing) handleAnalyze();
              }}
              className={clsx(
                'flex-1 px-4 py-2 text-sm font-medium transition-colors',
                activeTab === 'analyze'
                  ? 'border-b-2 border-hermit-600 text-hermit-600'
                  : 'text-gray-500 hover:text-gray-700'
              )}
            >
              <BarChart3 className="mr-1.5 inline h-4 w-4" />
              Analyze
            </button>
          </div>

          {/* Content */}
          {activeTab === 'chat' ? (
            <>
              {/* Messages */}
              <div className="flex-1 overflow-y-auto p-4 space-y-4">
                {messages.map((message) => (
                  <div
                    key={message.id}
                    className={clsx(
                      'flex gap-3',
                      message.role === 'user' ? 'flex-row-reverse' : ''
                    )}
                  >
                    <div
                      className={clsx(
                        'flex h-8 w-8 flex-shrink-0 items-center justify-center rounded-full',
                        message.role === 'user'
                          ? 'bg-hermit-100 text-hermit-600'
                          : 'bg-gray-100 text-gray-600'
                      )}
                    >
                      {message.role === 'user' ? (
                        <User className="h-4 w-4" />
                      ) : (
                        <Bot className="h-4 w-4" />
                      )}
                    </div>
                    <div className="flex flex-col gap-2 max-w-[280px]">
                      <div
                        className={clsx(
                          'rounded-lg px-3 py-2 text-sm',
                          message.role === 'user'
                            ? 'bg-hermit-600 text-white'
                            : 'bg-gray-100 text-gray-800'
                        )}
                      >
                        <p className="whitespace-pre-wrap">
                          {message.content}
                          {isStreaming && message.role === 'assistant' && message.id === messages[messages.length - 1]?.id && (
                            <span className="inline-block w-2 h-4 ml-0.5 bg-hermit-500 animate-pulse" />
                          )}
                        </p>
                      </div>
                      {/* Action buttons */}
                      {message.actions && message.actions.length > 0 && (
                        <div className="flex flex-wrap gap-1.5">
                          {message.actions.map((action, idx) => (
                            <button
                              key={idx}
                              onClick={() => {
                                if (action.step) {
                                  goToStep(action.step);
                                  setIsOpen(false);
                                } else if (action.action === 'analyze') {
                                  setActiveTab('analyze');
                                  if (!analysis) handleAnalyze();
                                } else if (action.action === 'addNode' && action.nodeType) {
                                  const labels: Record<string, string> = {
                                    gateway: 'Gateway',
                                    compute: 'Compute node',
                                    storage: 'Storage node',
                                    daq: 'DAQ node',
                                  };
                                  addNode(action.nodeType);
                                  success(`Added ${labels[action.nodeType]} to configuration`);
                                }
                              }}
                              className={clsx(
                                'inline-flex items-center gap-1 rounded-full px-2.5 py-1 text-xs font-medium transition-colors',
                                action.action === 'addNode'
                                  ? 'bg-green-50 text-green-700 hover:bg-green-100'
                                  : 'bg-hermit-50 text-hermit-700 hover:bg-hermit-100'
                              )}
                            >
                              {action.action === 'addNode' ? (
                                <Plus className="h-3 w-3" />
                              ) : action.step ? (
                                <ArrowRight className="h-3 w-3" />
                              ) : (
                                <Zap className="h-3 w-3" />
                              )}
                              {action.label}
                            </button>
                          ))}
                        </div>
                      )}
                    </div>
                  </div>
                ))}
                {isLoading && !isStreaming && (
                  <div className="flex gap-3">
                    <div className="flex h-8 w-8 items-center justify-center rounded-full bg-gray-100 text-gray-600">
                      <Bot className="h-4 w-4" />
                    </div>
                    <div className="flex items-center gap-2 rounded-lg bg-gray-100 px-3 py-2 text-sm text-gray-600">
                      <Loader2 className="h-4 w-4 animate-spin" />
                      Connecting...
                    </div>
                  </div>
                )}
                <div ref={messagesEndRef} />
              </div>

              {/* Quick Suggestions */}
              {!isLoading && messages.length <= 2 && (
                <div className="border-t border-gray-100 px-3 py-2">
                  <div className="flex flex-wrap gap-1.5">
                    {STEP_SUGGESTIONS[currentStep as WizardStepName]?.slice(0, 2).map((suggestion, idx) => (
                      <button
                        key={idx}
                        onClick={() => setInput(suggestion)}
                        className="rounded-full bg-gray-100 px-2.5 py-1 text-xs text-gray-600 hover:bg-gray-200 transition-colors"
                      >
                        {suggestion}
                      </button>
                    ))}
                  </div>
                </div>
              )}

              {/* Input */}
              <div className="border-t border-gray-200 p-3">
                <div className="flex gap-2">
                  <input
                    type="text"
                    value={input}
                    onChange={(e) => setInput(e.target.value)}
                    onKeyDown={handleKeyDown}
                    placeholder="Ask about your datacenter..."
                    className="flex-1 rounded-lg border border-gray-300 px-3 py-2 text-sm focus:border-hermit-500 focus:outline-none focus:ring-1 focus:ring-hermit-500"
                    disabled={isLoading}
                  />
                  {isStreaming ? (
                    <Button
                      onClick={handleCancelStream}
                      variant="outline"
                      className="px-3 border-red-300 text-red-600 hover:bg-red-50"
                    >
                      <X className="h-4 w-4" />
                    </Button>
                  ) : (
                    <Button
                      onClick={handleSend}
                      disabled={!input.trim() || isLoading}
                      className="px-3"
                    >
                      <Send className="h-4 w-4" />
                    </Button>
                  )}
                </div>
                <p className="mt-2 text-center text-xs text-gray-400">
                  {isStreaming ? 'Streaming response... click X to stop' : 'Try: "I need a research cluster" or "Add more compute nodes"'}
                </p>
              </div>
            </>
          ) : (
            /* Analysis Tab */
            <div className="flex-1 overflow-y-auto p-4">
              {isAnalyzing ? (
                <div className="flex flex-col items-center justify-center h-full gap-3 text-gray-500">
                  <Loader2 className="h-8 w-8 animate-spin" />
                  <p className="text-sm">Analyzing configuration...</p>
                </div>
              ) : analysis ? (
                <div className="space-y-4">
                  {/* Score */}
                  <div className="text-center">
                    <div className={clsx('text-4xl font-bold', getScoreColor(analysis.score))}>
                      {analysis.score}
                    </div>
                    <div className="text-sm text-gray-500">Configuration Score</div>
                  </div>

                  {/* Summary */}
                  <div className="rounded-lg bg-gray-50 p-3 text-sm text-gray-700">
                    {analysis.summary}
                  </div>

                  {/* Categories */}
                  {analysis.categories.length > 0 && (
                    <div>
                      <h4 className="mb-2 text-sm font-medium text-gray-700">Categories</h4>
                      <div className="space-y-2">
                        {analysis.categories.map((cat, i) => (
                          <div
                            key={i}
                            className="flex items-center gap-2 rounded-lg border border-gray-200 p-2"
                          >
                            {getStatusIcon(cat.status)}
                            <div className="flex-1">
                              <div className="flex items-center justify-between">
                                <span className="text-sm font-medium">{cat.name}</span>
                                <span className={clsx('text-sm', getScoreColor(cat.score))}>
                                  {cat.score}
                                </span>
                              </div>
                              <p className="text-xs text-gray-500">{cat.details}</p>
                            </div>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}

                  {/* Recommendations */}
                  {analysis.recommendations.length > 0 && (
                    <div>
                      <h4 className="mb-2 text-sm font-medium text-gray-700">Recommendations</h4>
                      <div className="space-y-2">
                        {analysis.recommendations.slice(0, 3).map((rec, i) => (
                          <div
                            key={i}
                            className="rounded-lg border border-gray-200 p-2"
                          >
                            <div className="flex items-center gap-2">
                              <Zap
                                className={clsx(
                                  'h-4 w-4',
                                  rec.priority === 'high'
                                    ? 'text-red-500'
                                    : rec.priority === 'medium'
                                    ? 'text-yellow-500'
                                    : 'text-blue-500'
                                )}
                              />
                              <span className="text-sm font-medium">{rec.suggestion}</span>
                            </div>
                            <p className="mt-1 text-xs text-gray-500">{rec.impact}</p>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}

                  {/* Warnings */}
                  {analysis.warnings.length > 0 && (
                    <div>
                      <h4 className="mb-2 text-sm font-medium text-gray-700">Warnings</h4>
                      <div className="space-y-1">
                        {analysis.warnings.map((warn, i) => (
                          <div
                            key={i}
                            className={clsx(
                              'flex items-center gap-2 rounded px-2 py-1 text-xs',
                              warn.severity === 'critical'
                                ? 'bg-red-50 text-red-700'
                                : warn.severity === 'warning'
                                ? 'bg-yellow-50 text-yellow-700'
                                : 'bg-blue-50 text-blue-700'
                            )}
                          >
                            <AlertTriangle className="h-3 w-3" />
                            {warn.message}
                          </div>
                        ))}
                      </div>
                    </div>
                  )}

                  {/* Refresh button */}
                  <Button
                    onClick={handleAnalyze}
                    disabled={isAnalyzing}
                    variant="outline"
                    className="w-full"
                  >
                    <BarChart3 className="mr-2 h-4 w-4" />
                    Re-analyze Configuration
                  </Button>
                </div>
              ) : (
                <div className="flex flex-col items-center justify-center h-full gap-3 text-gray-500">
                  <BarChart3 className="h-8 w-8" />
                  <p className="text-sm">Click to analyze your configuration</p>
                  <Button onClick={handleAnalyze}>Analyze Now</Button>
                </div>
              )}
            </div>
          )}
        </div>
      )}
    </>
  );
}
